import SidePanel from '../SidePanel.jsx';
import Modal from '../Modal';
import DateTimeInput from '../DateTimeInput.jsx';
import BookableResourcesSelect from '../BookableResourcesSelect.jsx';
import { useEffect, useState } from 'react';
import Toggle from '../Toggle.jsx';
import Select from '../Select';
import { addDays, addMonths, addWeeks, endOfDay, endOfMonth, endOfWeek, subDays } from 'date-fns';
import { format } from 'date-fns-tz';
import {
    formatDate,
    formatDateTime,
    formatInternalDateTime,
    formatTime,
    getBrowserTimezone,
    humanDaysOfWeek, weekdayOfMonth
} from '../../lib/DateTime.js';
import InfoMessageBlock from '../InfoMessageBlock.jsx';
import { randomString } from '../../lib/Utils.js';
import ErrorMessageBlock from '../ErrorMessageBlock.jsx';
import TextInput from '~/components/TextInput';
import { clientFormatDateTime, clientFormatTime } from '~/public/lib/ClientDateTime.js';

const EVERYDAY_DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAYS_DAYS_OF_WEEK = [1, 2, 3, 4, 5];

const REPEAT_EVERYDAY = 'everyday-1';
const REPEAT_WEEKDAYS = 'weekdays-1';

function areWeekdaysSame(left, right) {
    return JSON.stringify(left.sort()) === JSON.stringify(right.sort());
}

const DayOfWeekToggle = ({ isSelected, onClick, children }) => {
    return (
        <button
            type="button"
            className={`rounded-md border bg-white w-[68px] h-[68px] ${isSelected ? 'ring-1 ring-primary-700 border-primary-700 text-primary-700' : 'border-gray-300 text-black'} font-medium flex items-center justify-center outline-primary-700`}
            onClick={onClick}
        >
            {children}
        </button>
    );
};

const DayOfWeekSelection = ({ value, onChange, className }) => {
    const startDate = new Date('2024-08-11 12:00');
    const days = [];
    for (let i = 0; i < 7; i++) {
        days[i] = format(addDays(startDate, i), 'EEEEEE');
    }

    function toggle(day) {
        const i = value.indexOf(day);
        if (i < 0) {
            onChange([...value, day]);
        } else {
            const newValue = [...value];
            newValue.splice(i, 1);
            onChange(newValue);
        }
    }

    return (
        <div className={`flex justify-between ${className}`}>
            {days.map((day, idx) => (
                <DayOfWeekToggle
                    isSelected={value.indexOf(idx) > -1}
                    key={idx}
                    onClick={() => toggle(idx)}
                >
                    {day}
                </DayOfWeekToggle>
            ))}
        </div>
    );
};

const AddClassSessionsPanel = ({
    isOpen,
    isSeries,
    addToGroupKey,
    onClose,
    instances,
    setInstances,
    defaultMaxAttendees,
}) => {
    const [newStart, setNewStart] = useState('');
    const [newBookableResource, setNewBookableResource] = useState(null);
    const [isRecurring, setIsRecurring] = useState(false);
    const [repeatType, setRepeatType] = useState('weekly-1');
    const [daysOfWeek, setDaysOfWeek] = useState([]);
    const [maxAttendees, setMaxAttendees] = useState(0);
    const [repeatCount, setRepeatCount] = useState('8');
    const [showTimezoneDiffersWarning, setShowTimezoneDiffersWarning] =
        useState(false);
    const [resourceTimezone, setResourceTimezone] = useState('');
    const [sessionGroupKey, setSessionGroupKey] = useState(addToGroupKey);
    const [showEditMaxAttendees, setShowEditMaxAttendees] = useState(false);

    const isDirty = newStart;

    const [repeatUnit, repeatFrequency] = repeatType.split('-');
    const repeatCountOptions = {};

    const repeatCountWord = repeatUnit === 'monthly' ? 'month' : 'week';
    for (let i = 1; i < 105; i++) {
        repeatCountOptions[i] = `${i} ${repeatCountWord}${i > 1 ? 's' : ''}`;
    }

    function reset(opts) {
        setNewStart('');
        setMaxAttendees(0);
        setShowEditMaxAttendees(false)
        //setNewBookableResource(null);
        setIsRecurring(false);
        setSessionGroupKey(opts?.groupKey || null);
    }

    useEffect(() => {
        if (isOpen) {
            reset({ groupKey: addToGroupKey });
        }
    }, [isOpen, addToGroupKey]);

    useEffect(() => {
        if (
            (repeatType === REPEAT_EVERYDAY &&
                !areWeekdaysSame(daysOfWeek, EVERYDAY_DAYS_OF_WEEK)) ||
            (repeatType === REPEAT_WEEKDAYS &&
                !areWeekdaysSame(daysOfWeek, WEEKDAYS_DAYS_OF_WEEK))
        ) {
            setRepeatType('weekly-1');
        }
    }, [daysOfWeek]);

    useEffect(() => {
        if (
            newStart &&
            daysOfWeek.length <= 1 &&
            daysOfWeek.indexOf(newStart.getDay()) < 0
        ) {
            // If there's only 1 day set make sure it switches to the current date
            setDaysOfWeek([newStart.getDay()]);
        }
    }, [newStart]);

    let repeatEndDate = null;
    if (repeatUnit === 'monthly') {
        repeatEndDate = endOfMonth(
            addMonths(newStart, (repeatCount - 1) * repeatFrequency),
        );
    } else {
        repeatEndDate = endOfWeek(
            addWeeks(newStart, (repeatCount - 1) * repeatFrequency),
        );
    }

    function getNewInstances() {
        const newInstances = [];
        const groupKey = isSeries ? sessionGroupKey || randomString(8) : null;

        if (isRecurring) {
            const end = subDays(endOfDay(repeatEndDate), 1); // Make it inclusive, the loop always adds 1 more day after it checks end
            let d = newStart;
            const monthlyDayOfWeek = weekdayOfMonth(newStart);
            let weekCount = 0;
            for (let i = 0; i < 9999 && d < end; i++) {
                d = addDays(newStart, i);

                if (i > 0 && d.getDay() === 0) {
                    weekCount += 1;
                }

                // Is it a selected day of week & not a week we're skipping?
                if (repeatUnit === 'monthly') {
                    if (monthlyDayOfWeek === weekdayOfMonth(d)) {
                        newInstances.push({
                            start: d,
                            bookable_resource: newBookableResource,
                            group_key: groupKey,
                            max_attendees: maxAttendees,
                        });
                    }
                } else {
                    if (
                        weekCount % repeatFrequency === 0 &&
                        daysOfWeek.indexOf(d.getDay()) > -1
                    ) {
                        newInstances.push({
                            start: d,
                            bookable_resource: newBookableResource,
                            group_key: groupKey,
                            max_attendees: maxAttendees,
                        });
                    }
                }
            }
        } else {
            newInstances.push({
                start: newStart,
                bookable_resource: newBookableResource,
                group_key: groupKey,
                max_attendees: maxAttendees,
            });
        }

        console.log('adding new instances', newInstances);
        return newInstances;
    }

    function doAdd(onDone) {
        let groupKey = '';
        if (newStart && newBookableResource) {
            const newInstances = getNewInstances();
            setInstances([...instances, ...newInstances]);

            if (newInstances.length > 0) {
                groupKey = newInstances[0].group_key;
            }
        }

        onDone({ groupKey: groupKey });
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                console.log('newStart', newStart);
                if (
                    !isDirty ||
                    confirm(
                        'You have unsaved changes. Are you sure you want to discard them?',
                    )
                ) {
                    onClose();
                }
            }}
            widthClass="sm:max-w-[600px]"
        >
            <h2>Add Session{isRecurring ? 's' : ''}</h2>
            <div className="input-label">Book with</div>
            <BookableResourcesSelect
                value={newBookableResource}
                inputClassName="w-full"
                onChange={(newValue, resourceDetails) => {
                    setNewBookableResource(newValue);
                    console.log('resourcetz = ', resourceDetails.timezone);
                    setResourceTimezone(resourceDetails.timezone);
                    setShowTimezoneDiffersWarning(
                        resourceDetails.timezone !== getBrowserTimezone(),
                    );
                }}
            />

            <div className="mt-4">
                {!showEditMaxAttendees && (
                    <div>
                        <div className="input-label">Max Attendees</div>
                        <div className="panel px-4 py-2">
                            {defaultMaxAttendees}
                            <button
                                type="button"
                                className="btn-light-pill-sm uppercase ml-4"
                                onClick={() => {
                                    setMaxAttendees(defaultMaxAttendees);
                                    setShowEditMaxAttendees(true);
                                }}
                            >
                                Change
                            </button>
                        </div>
                    </div>
                )}
                {showEditMaxAttendees && (
                    <TextInput
                        value={maxAttendees}
                        onChange={setMaxAttendees}
                        autoFocus={true}
                        label="Max Attendees"
                        type="number"
                    />
                )}
            </div>

            <DateTimeInput
                value={newStart}
                onChange={setNewStart}
                className="mt-4 w-full justify-between"
                dateLabel="Start"
                timeLabel="&nbsp;"
            />
            {!!newStart && new Date(newStart) < new Date() && (
                <ErrorMessageBlock className="mt-1">
                    This date and time are in the past, clients will not be able
                    to book this.
                </ErrorMessageBlock>
            )}
            {showTimezoneDiffersWarning && (
                <InfoMessageBlock className="mt-1" showIcon={true}>
                    {!!newStart && (
                        <div className="mb-2">
                            <strong>This will start at {clientFormatTime(newStart.toISOString(), resourceTimezone)}</strong><br />
                            {clientFormatTime(newStart.toISOString(), getBrowserTimezone())} in your time zone ({getBrowserTimezone()}) is {clientFormatTime(newStart.toISOString(), resourceTimezone)} in the business's time zone ({resourceTimezone}).
                        </div>
                    )}

                    All times are entered in <strong>your local time zone</strong> and automatically converted to the <strong>business's time zone</strong> after saving.
                </InfoMessageBlock>
            )}

            <div className="flex justify-end mt-1">
                <div className="mr-2 text-gray-500 text-sm">
                    Make this recurring
                </div>
                <Toggle enabled={isRecurring} setEnabled={setIsRecurring} />
            </div>

            <div
                className={`transition-all overflow-hidden duration-300 -mx-1 px-1 ${isRecurring ? 'max-h-72 opacity-100 pb-1' : 'max-h-0 opacity-0'}`}
            >
                <Select
                    label="Repeat"
                    value={repeatType}
                    onChange={newValue => {
                        setRepeatType(newValue);
                        if (newValue === REPEAT_EVERYDAY) {
                            setDaysOfWeek(EVERYDAY_DAYS_OF_WEEK);
                        } else if (newValue === REPEAT_WEEKDAYS) {
                            setDaysOfWeek(WEEKDAYS_DAYS_OF_WEEK);
                        }
                    }}
                    options={{
                        'weekly-1': 'Weekly',
                        'weekly-2': 'Every other week',
                        'weekly-3': 'Every 3 weeks',
                        'weekly-4': 'Every 4 weeks',
                        [REPEAT_EVERYDAY]: 'Every day',
                        [REPEAT_WEEKDAYS]: 'Weekdays',
                        'monthly-1': newStart ? `Monthly on the ${weekdayOfMonth(newStart)}` : 'Monthly',
                    }}
                    className="mb-4"
                />

                {repeatUnit === 'monthly' && newStart && Math.ceil(newStart.getDate() / 7) === 5 && (
                    <InfoMessageBlock className="mt-1 mb-4" showIcon={true}>
                        <strong>Note:</strong> This is scheduled for the 5th {format(newStart, 'EEEE')} of the month.
                        Months without a 5th occurrence will be automatically skipped.
                    </InfoMessageBlock>
                )}

                <Select
                    label={repeatType === 'weekly-1' ? 'For the next' : 'For'}
                    value={repeatCount}
                    onChange={setRepeatCount}
                    options={repeatCountOptions}
                />

                {repeatUnit !== 'monthly' && (
                    <DayOfWeekSelection
                        value={daysOfWeek}
                        onChange={newValue => {
                            if (
                                newStart &&
                                newValue.indexOf(newStart.getDay()) < 0
                            ) {
                                setDaysOfWeek([...newValue, newStart.getDay()]);
                            } else {
                                setDaysOfWeek(newValue);
                            }
                        }}
                        className="mt-4"
                    />
                )}

                {!!newStart && (
                    <div className="mt-1 text-gray-500 text-sm">
                        This will take place
                        {repeatUnit === 'monthly' && (
                            <span> on the {weekdayOfMonth(newStart)} of every month</span>
                        )}
                        {repeatUnit !== 'monthly' && (
                            <span> on {humanDaysOfWeek(daysOfWeek)}</span>
                        )}
                        {' '}
                        at {formatTime(newStart)} starting on{' '}
                        {formatDate(newStart)} and ending{' '}
                        {formatDate(repeatEndDate)}
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between mt-4">
                <div className="flex items-center">
                    <button className="btn-pill" onClick={() => doAdd(onClose)}>
                        Save
                    </button>

                    <button
                        className="btn-light-pill ml-4"
                        onClick={() => doAdd(reset)}
                    >
                        Save &amp; Add New
                    </button>
                </div>

                <button className="btn-gray-pill btn-lg" onClick={onClose}>
                    Cancel
                </button>
            </div>
        </Modal>
    );
};

export default AddClassSessionsPanel;
